import { useState, useEffect } from 'react';
import { LandingPage } from '@/landing';
import { LoginPage } from './components/LoginPage';
import { ResetPasswordPage } from './components/ResetPasswordPage';
import { InnovatorDashboard } from './components/InnovatorDashboard';
import { ReviewerDashboard } from './components/ReviewerDashboard';
import { ReviewerCompleteProfile } from './components/ReviewerCompleteProfile';
import { AdminDashboard } from './components/AdminDashboard';
import { AdminAssignmentsDashboard } from './components/AdminAssignmentsDashboard';
import { InstitutionalAdminDashboard } from './components/InstitutionalAdminDashboard';
import { BulkUploadPage } from './components/BulkUploadPage';
import { SuperAdminInstitutionsPage } from './components/SuperAdminInstitutionsPage';
import { ReviewInterface } from './components/ReviewInterface';
import { SubmitInnovation } from './components/SubmitInnovation';
import { InnovatorFilingDocPaystackPage } from './components/InnovatorFilingDocPaystackPage';
import { AccountSettings } from './components/AccountSettings';
import { Navbar } from './components/Navbar';
import { triggerWake } from './api/backendWake';

export type UserRole = 'innovator' | 'reviewer' | 'institution' | 'admin' | 'super_admin' | 'institutional_admin' | null;
export type PageView =
  | 'landing'
  | 'login'
  | 'dashboard'
  | 'review'
  | 'submit'
  | 'settings'
  | 'reviewer-complete-profile'
  | 'reviewer-profile-edit'
  | 'admin-assignments'
  | 'super-admin-institutions'
  | 'institutional-bulk-upload'
  | 'filing-doc-prep-pay';

export interface User {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  institution?: string;
  full_name?: string;
}

function getResetTokenFromUrl(): string | null {
  if (typeof window === 'undefined') return null;
  const pathname = (window.location.pathname || '').replace(/\/+$/, '') || '/';
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token');
  const isResetPath =
    pathname === '/reset-password' ||
    pathname.endsWith('/reset-password') ||
    ((pathname === '/' || pathname === '') && !!token);
  if (token && token.length >= 16 && isResetPath) return token;
  return null;
}

function App() {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [currentPage, setCurrentPage] = useState<PageView>('landing');
  const [selectedInnovationId, setSelectedInnovationId] = useState<string | null>(null);
  const [reviewAssessmentMode, setReviewAssessmentMode] = useState<'ai' | 'manual'>('manual');
  const [resetPasswordToken, setResetPasswordToken] = useState<string | null>(getResetTokenFromUrl);

  useEffect(() => {
    const token = getResetTokenFromUrl();
    if (token && !resetPasswordToken) setResetPasswordToken(token);
  }, []);

  // Email verification (and bookmarks) send users to /login — sync URL with app state
  useEffect(() => {
    const pathname = (window.location.pathname || '').replace(/\/+$/, '') || '/';
    if (pathname === '/login' || pathname.endsWith('/login')) {
      setCurrentPage('login');
    }
  }, []);

  const handleLogin = (user: User) => {
    setCurrentUser(user);
    setCurrentPage('dashboard');
  };

  const handleLogout = () => {
    setCurrentUser(null);
    setCurrentPage('landing');
    setSelectedInnovationId(null);
    setReviewAssessmentMode('manual');
  };

  const handleNavigate = (
    page: PageView,
    innovationId?: string,
    opts?: { assessmentMode?: 'ai' | 'manual' }
  ) => {
    if (page === 'filing-doc-prep-pay' && currentUser && currentUser.role !== 'innovator') {
      setCurrentPage('dashboard');
      return;
    }
    setCurrentPage(page);
    if (innovationId !== undefined) {
      setSelectedInnovationId(innovationId);
    }
    if (page === 'review') {
      setReviewAssessmentMode(opts?.assessmentMode ?? 'manual');
    } else {
      setReviewAssessmentMode('manual');
    }
  };

  const handleResetPasswordDone = () => {
    setResetPasswordToken(null);
    setCurrentPage('login');
    window.history.replaceState({}, '', window.location.pathname.replace(/\/reset-password\/?$/, '') || '/');
  };

  const renderPage = () => {
    if (resetPasswordToken) {
      return (
        <ResetPasswordPage
          token={resetPasswordToken}
          onSuccess={handleResetPasswordDone}
          onInvalidToken={handleResetPasswordDone}
        />
      );
    }

    if (!currentUser && currentPage !== 'login' && currentPage !== 'landing') {
      return <LoginPage onLogin={handleLogin} />;
    }

    switch (currentPage) {
      case 'landing':
        return (
          <LandingPage
            onGetStarted={() => {
              triggerWake(); // trigger keepalive as soon as user clicks Get started
              handleNavigate('login');
            }}
          />
        );
      
      case 'login':
        return <LoginPage onLogin={handleLogin} onBackToLanding={() => handleNavigate('landing')} />;
      
      case 'submit':
        return (
          <SubmitInnovation
            user={currentUser!}
            onBack={() => handleNavigate('dashboard')}
          />
        );

      case 'filing-doc-prep-pay':
        return (
          <InnovatorFilingDocPaystackPage user={currentUser!} onBack={() => handleNavigate('dashboard')} />
        );

      case 'review':
        return (
          <ReviewInterface
            innovationId={selectedInnovationId!}
            reviewer={currentUser!}
            assessmentMode={reviewAssessmentMode}
            onBack={() => handleNavigate('dashboard')}
          />
        );
      
      case 'reviewer-complete-profile':
        return (
          <ReviewerCompleteProfile
            user={currentUser!}
            mode="complete"
            onSuccess={() => handleNavigate('dashboard')}
            onBack={() => handleNavigate('dashboard')}
          />
        );
      case 'reviewer-profile-edit':
        return (
          <ReviewerCompleteProfile
            user={currentUser!}
            mode="edit"
            onSuccess={() => handleNavigate('dashboard')}
            onBack={() => handleNavigate('dashboard')}
          />
        );
      case 'dashboard':
        if (currentUser?.role === 'reviewer') {
          return (
            <ReviewerDashboard
              user={currentUser}
              onReviewClick={(id, opts) => handleNavigate('review', id, opts)}
              onNavigateToCompleteProfile={() => handleNavigate('reviewer-complete-profile')}
              onNavigateToProfileEdit={() => handleNavigate('reviewer-profile-edit')}
            />
          );
        } else if (currentUser?.role === 'institutional_admin') {
          return (
            <InstitutionalAdminDashboard
              user={currentUser}
              onNavigateToBulkUpload={() => handleNavigate('institutional-bulk-upload')}
            />
          );
        } else if (currentUser?.role === 'admin' || currentUser?.role === 'super_admin') {
          return (
            <AdminDashboard
              user={currentUser}
              onReviewClick={(id) => handleNavigate('review', id)}
              onNavigateToAssignments={() => handleNavigate('admin-assignments')}
              onNavigateToInstitutionApplications={() => handleNavigate('super-admin-institutions')}
              isSuperAdmin={currentUser?.role === 'super_admin'}
            />
          );
        } else {
          return (
            <InnovatorDashboard
              user={currentUser!}
              onSubmitClick={() => handleNavigate('submit')}
              onReviewClick={(id) => handleNavigate('review', id)}
              onPayFilingDocBilling={() => handleNavigate('filing-doc-prep-pay')}
            />
          );
        }
      
      case 'admin-assignments':
        return (
          <AdminAssignmentsDashboard
            user={currentUser!}
            onBack={() => handleNavigate('dashboard')}
            onReviewClick={(id) => handleNavigate('review', id)}
          />
        );
      case 'super-admin-institutions':
        return (
          <SuperAdminInstitutionsPage
            user={currentUser!}
            onBack={() => handleNavigate('dashboard')}
          />
        );
      case 'institutional-bulk-upload':
        return (
          <BulkUploadPage
            user={currentUser!}
            onBack={() => handleNavigate('dashboard')}
          />
        );
      case 'settings':
        return (
          <AccountSettings
            user={currentUser!}
            onBack={() => handleNavigate('dashboard')}
          />
        );
      
      default:
        return (
          <LandingPage
            onGetStarted={() => {
              triggerWake();
              handleNavigate('login');
            }}
          />
        );
    }
  };

  return (
    <div className={currentPage !== 'landing' ? `app-platform min-h-screen${currentUser ? ' bg-[var(--off-white)]' : ''}` : 'min-h-screen'}>
      {currentUser && currentPage !== 'landing' && (
        <Navbar
          user={currentUser}
          onLogout={handleLogout}
          onNavigate={handleNavigate}
          currentPage={currentPage}
        />
      )}
      <main className={currentUser && currentPage !== 'landing' ? 'pt-16' : ''}>
        {renderPage()}
      </main>
    </div>
  );
}

export default App;